import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Upload, Trash2, Eye, EyeOff, Star, ArrowUp, ArrowDown, ExternalLink, Loader2, FolderOpen, Images, Video, Pencil, Check, X, FolderInput } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { api } from '../../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const IMG_EXT = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'];
const VID_EXT = ['mp4', 'mov', 'avi', 'webm'];

/** Upload many photos/videos into an album, one request each so a single bad file doesn't sink the batch. */
function useAlbumUploader(token, albumId, onDone) {
  const [progress, setProgress] = useState(null); // {done,total,current}
  const run = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    let ok = 0; const failed = [];
    setProgress({ done: 0, total: files.length, current: files[0].name });
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      const ext = f.name.split('.').pop().toLowerCase();
      const isVideo = VID_EXT.includes(ext);
      if (![...IMG_EXT, ...VID_EXT].includes(ext)) { failed.push(`${f.name}: unsupported type`); continue; }
      if (f.size > (isVideo ? 1024 ** 3 : 30 * 1024 ** 2)) { failed.push(`${f.name}: too large`); continue; }
      setProgress({ done: i, total: files.length, current: f.name });
      try {
        const fd = new FormData(); fd.append('file', f);
        const res = await fetch(`${BACKEND_URL}/api/admin/upload?category=gallery&album_id=${encodeURIComponent(albumId)}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
        const j = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(j.detail || 'Upload failed');
        ok += 1;
      } catch (e) { failed.push(`${f.name}: ${e.message}`); }
    }
    setProgress(null);
    if (ok) toast.success(`${ok} file${ok === 1 ? '' : 's'} added to the album`);
    if (failed.length) toast.error(`${failed.length} failed`, { description: failed.slice(0, 3).join(' · ') });
    onDone && onDone();
  };
  return { run, progress };
}

function AlbumCard({ a, onOpen, onPublish, onUnpublish, onDelete, canPublish }) {
  return (
    <Card className="overflow-hidden" data-testid={`gallery-album-card-${a.album_id}`}>
      <button type="button" onClick={() => onOpen(a)} className="w-full text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-[hsl(var(--gs-navy))]" aria-label={`Open album ${a.title}`}>
        <div className="aspect-[4/3] bg-[hsl(var(--muted)/0.4)] relative">
          {a.cover_url ? <img src={`${BACKEND_URL}${a.cover_url}`} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><FolderOpen className="w-10 h-10 text-[hsl(var(--muted-foreground))]" /></div>}
          <Badge className={`absolute top-2 left-2 ${a.is_published ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`} data-testid={`gallery-album-status-${a.album_id}`}>{a.is_published ? 'Published' : 'Draft'}</Badge>
        </div>
      </button>
      <CardContent className="p-4">
        <div className="font-semibold truncate" data-testid={`gallery-album-title-${a.album_id}`}>{a.title}</div>
        <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5 flex items-center gap-3">
          <span className="flex items-center gap-1"><Images className="w-3 h-3" />{a.photo_count}</span>
          {a.video_count > 0 && <span className="flex items-center gap-1"><Video className="w-3 h-3" />{a.video_count}</span>}
          {a.event_date && <span>{a.event_date}</span>}
        </div>
        <div className="flex gap-2 mt-3 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => onOpen(a)} data-testid={`gallery-album-open-${a.album_id}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Manage</Button>
          {canPublish && (a.is_published
            ? <Button size="sm" variant="outline" onClick={() => onUnpublish(a)} data-testid={`gallery-album-unpublish-${a.album_id}`}><EyeOff className="w-3.5 h-3.5 mr-1" /> Unpublish</Button>
            : <Button size="sm" className="bg-[hsl(var(--gs-navy))]" onClick={() => onPublish(a)} data-testid={`gallery-album-publish-${a.album_id}`}><Eye className="w-3.5 h-3.5 mr-1" /> Publish</Button>)}
          {canPublish && <Button size="sm" variant="ghost" className="text-[hsl(var(--destructive))]" onClick={() => onDelete(a)} aria-label="Delete album" data-testid={`gallery-album-delete-${a.album_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
        </div>
      </CardContent>
    </Card>
  );
}

function AlbumEditor({ token, album, albums, canPublish, onBack, onChanged }) {
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState({ title: album.title, description: album.description || '', event_date: album.event_date || '' });
  const [savingMeta, setSavingMeta] = useState(false);
  const [editingCaption, setEditingCaption] = useState(null); // {file_id, caption}
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try { setData(await api.getAuth(`/api/admin/gallery/albums/${album.album_id}/files`, token)); } catch (e) { toast.error(e.message); }
  }, [album.album_id, token]);
  useEffect(() => { load(); }, [load]);

  const { run: upload, progress } = useAlbumUploader(token, album.album_id, () => { load(); onChanged(); });

  const saveMeta = async () => {
    setSavingMeta(true);
    try { await api.putAuth(`/api/admin/gallery/albums/${album.album_id}`, meta, token); toast.success('Album details saved'); onChanged(); }
    catch (e) { toast.error(e.message); }
    setSavingMeta(false);
  };
  const patchFile = async (file_id, body, msg) => {
    try { await api.putAuth(`/api/admin/gallery/files/${file_id}`, body, token); if (msg) toast.success(msg); load(); onChanged(); }
    catch (e) { toast.error(e.message); }
  };
  const setCover = async (file_id) => {
    try { await api.putAuth(`/api/admin/gallery/albums/${album.album_id}`, { cover_file_id: file_id }, token); toast.success('Cover photo updated'); load(); onChanged(); }
    catch (e) { toast.error(e.message); }
  };
  const move = async (idx, delta) => {
    const items = [...(data?.items || [])];
    const j = idx + delta; if (j < 0 || j >= items.length) return;
    [items[idx], items[j]] = [items[j], items[idx]];
    setData({ ...data, items });
    try { await api.postAuth(`/api/admin/gallery/albums/${album.album_id}/reorder`, { file_ids: items.map(i => i.file_id) }, token); } catch (e) { toast.error(e.message); load(); }
  };
  const removeFile = async (it) => {
    if (!window.confirm('Remove this photo from the gallery? It will be deleted from the media library.')) return;
    try { await api.deleteAuth(`/api/admin/files/${it.file_id}`, token); toast.success('Photo removed'); load(); onChanged(); } catch (e) { toast.error(e.message || 'Only admin/chairman can delete files'); }
  };

  const items = data?.items || [];
  const coverId = data?.album?.cover_file_id;

  return (
    <div className="space-y-5" data-testid="gallery-album-editor">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={onBack} data-testid="gallery-editor-back">← All albums</Button>
        <div className="flex items-center gap-2">
          <Badge className={data?.album?.is_published ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>{data?.album?.is_published ? 'Published' : 'Draft — not visible to the public'}</Badge>
          {data?.album?.is_published && <a href={`/gallery/${album.album_id}`} target="_blank" rel="noreferrer" className="text-sm underline flex items-center gap-1" data-testid="gallery-editor-view-public">View <ExternalLink className="w-3.5 h-3.5" /></a>}
        </div>
      </div>

      <div className="grid lg:grid-cols-[360px_1fr] gap-5">
        <Card><CardContent className="p-5 space-y-3">
          <div className="font-medium">Album details</div>
          <div><Label htmlFor="alb-title">Title *</Label><Input id="alb-title" value={meta.title} onChange={e => setMeta({ ...meta, title: e.target.value })} data-testid="gallery-editor-title" /></div>
          <div><Label htmlFor="alb-date">Event date</Label><Input id="alb-date" type="date" value={meta.event_date} onChange={e => setMeta({ ...meta, event_date: e.target.value })} data-testid="gallery-editor-date" /></div>
          <div><Label htmlFor="alb-desc">Description</Label><Textarea id="alb-desc" rows={3} value={meta.description} onChange={e => setMeta({ ...meta, description: e.target.value })} data-testid="gallery-editor-description" /></div>
          <Button onClick={saveMeta} disabled={savingMeta || !meta.title.trim()} className="w-full bg-[hsl(var(--gs-navy))]" data-testid="gallery-editor-save-meta">{savingMeta ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />} Save details</Button>

          <div className="pt-3 border-t">
            <div className="font-medium mb-2 flex items-center gap-2"><Upload className="w-4 h-4" /> Add photos & videos</div>
            <input ref={inputRef} type="file" multiple className="hidden" accept="image/*,.heic,.heif,video/*" onChange={(e) => { upload(e.target.files); e.target.value = ''; }} data-testid="gallery-editor-file-input" />
            <Button type="button" variant="outline" className="w-full" disabled={!!progress} onClick={() => inputRef.current?.click()} data-testid="gallery-editor-upload-button">
              {progress ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading {progress.done + 1} of {progress.total}…</> : <><Plus className="w-4 h-4 mr-2" /> Choose files</>}
            </Button>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2">Select many at once. iPhone HEIC photos are converted automatically. Images up to 30MB, videos up to 1GB.</p>
            {progress && <p className="text-xs mt-1 truncate" data-testid="gallery-upload-progress">{progress.current}</p>}
          </div>
        </CardContent></Card>

        <div>
          {data === null ? <div className="grid grid-cols-2 md:grid-cols-3 gap-3">{[0, 1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="aspect-square rounded-lg" />)}</div>
            : items.length === 0 ? (
              <Card className="p-10 text-center" data-testid="gallery-editor-empty"><Images className="w-10 h-10 mx-auto text-[hsl(var(--muted-foreground))]" /><p className="mt-3 text-[hsl(var(--muted-foreground))]">No photos yet — use “Choose files” to add the first ones.</p></Card>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="gallery-editor-grid">
                {items.map((it, idx) => (
                  <Card key={it.file_id} className={`overflow-hidden ${it.is_hidden ? 'opacity-60' : ''}`} data-testid={`gallery-item-${it.file_id}`}>
                    <div className="aspect-square bg-[hsl(var(--muted)/0.4)] relative">
                      {it.kind === 'video' ? <video src={`${BACKEND_URL}${it.url}`} className="w-full h-full object-cover" muted /> : <img src={`${BACKEND_URL}${it.thumb_url}`} alt={it.caption || ''} className="w-full h-full object-cover" loading="lazy" />}
                      {coverId === it.file_id && <Badge className="absolute top-2 left-2 bg-[hsl(var(--gs-navy))] text-white"><Star className="w-3 h-3 mr-1" /> Cover</Badge>}
                      {it.is_hidden && <Badge className="absolute top-2 right-2 bg-slate-800 text-white">Hidden</Badge>}
                    </div>
                    <CardContent className="p-2 space-y-2">
                      {editingCaption?.file_id === it.file_id ? (
                        <div className="flex gap-1">
                          <Input value={editingCaption.caption} onChange={e => setEditingCaption({ ...editingCaption, caption: e.target.value })} className="h-8 text-sm" placeholder="Caption" autoFocus onKeyDown={e => { if (e.key === 'Enter') { patchFile(it.file_id, { caption: editingCaption.caption }, 'Caption saved'); setEditingCaption(null); } if (e.key === 'Escape') setEditingCaption(null); }} data-testid={`gallery-caption-input-${it.file_id}`} />
                          <Button size="sm" className="h-8 px-2 bg-[hsl(var(--gs-navy))]" onClick={() => { patchFile(it.file_id, { caption: editingCaption.caption }, 'Caption saved'); setEditingCaption(null); }} aria-label="Save caption" data-testid={`gallery-caption-save-${it.file_id}`}><Check className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setEditingCaption(null)} aria-label="Cancel"><X className="w-3.5 h-3.5" /></Button>
                        </div>
                      ) : (
                        <button type="button" className="w-full text-left text-xs min-h-[32px] px-1 rounded hover:bg-[hsl(var(--muted)/0.5)] truncate" onClick={() => setEditingCaption({ file_id: it.file_id, caption: it.caption || '' })} data-testid={`gallery-caption-${it.file_id}`}>
                          {it.caption ? it.caption : <span className="text-[hsl(var(--muted-foreground))] italic">Add caption…</span>}
                        </button>
                      )}
                      <div className="flex items-center justify-between gap-1">
                        <div className="flex gap-0.5">
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => move(idx, -1)} disabled={idx === 0} aria-label="Move earlier" data-testid={`gallery-move-up-${it.file_id}`}><ArrowUp className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => move(idx, 1)} disabled={idx === items.length - 1} aria-label="Move later" data-testid={`gallery-move-down-${it.file_id}`}><ArrowDown className="w-3.5 h-3.5" /></Button>
                        </div>
                        <div className="flex gap-0.5">
                          {it.kind === 'image' && <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setCover(it.file_id)} disabled={coverId === it.file_id} aria-label="Use as cover" title="Use as cover" data-testid={`gallery-set-cover-${it.file_id}`}><Star className="w-3.5 h-3.5" /></Button>}
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => patchFile(it.file_id, { is_hidden: !it.is_hidden }, it.is_hidden ? 'Photo shown' : 'Photo hidden from public')} aria-label={it.is_hidden ? 'Show' : 'Hide'} title={it.is_hidden ? 'Show' : 'Hide'} data-testid={`gallery-toggle-hidden-${it.file_id}`}>{it.is_hidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}</Button>
                          <MoveMenu it={it} albums={albums.filter(x => x.album_id !== album.album_id)} onMove={(aid) => patchFile(it.file_id, { album_id: aid }, 'Moved to another album')} />
                          {canPublish && <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-[hsl(var(--destructive))]" onClick={() => removeFile(it)} aria-label="Delete photo" data-testid={`gallery-delete-${it.file_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

function MoveMenu({ it, albums, onMove }) {
  const [open, setOpen] = useState(false);
  if (!albums.length) return null;
  return (
    <>
      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setOpen(true)} aria-label="Move to another album" title="Move to another album" data-testid={`gallery-move-album-${it.file_id}`}><FolderInput className="w-3.5 h-3.5" /></Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="gallery-move-dialog">
          <DialogHeader><DialogTitle>Move photo to…</DialogTitle><DialogDescription>Choose the album this photo should live in.</DialogDescription></DialogHeader>
          <Select onValueChange={(v) => { onMove(v); setOpen(false); }}>
            <SelectTrigger data-testid="gallery-move-select"><SelectValue placeholder="Select album" /></SelectTrigger>
            <SelectContent>{albums.map(a => <SelectItem key={a.album_id} value={a.album_id}>{a.title}</SelectItem>)}</SelectContent>
          </Select>
        </DialogContent>
      </Dialog>
    </>
  );
}

function UnsortedPanel({ token, albums, onChanged }) {
  const [items, setItems] = useState(null);
  const load = useCallback(async () => { try { setItems((await api.getAuth('/api/admin/gallery/unsorted', token)).items || []); } catch { setItems([]); } }, [token]);
  useEffect(() => { load(); }, [load]);
  if (!items || items.length === 0) return null;
  return (
    <Card data-testid="gallery-unsorted">
      <CardContent className="p-4">
        <div className="font-medium mb-1">Unsorted gallery photos ({items.length})</div>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">Uploaded to the “gallery” category but not in any album yet. Move them into an album to show them on the website.</p>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {items.map(it => (
            <div key={it.file_id} className="relative aspect-square rounded-md overflow-hidden bg-[hsl(var(--muted)/0.4)] group">
              {it.kind === 'video' ? <div className="w-full h-full flex items-center justify-center"><Video className="w-6 h-6" /></div> : <img src={`${BACKEND_URL}${it.thumb_url}`} alt="" className="w-full h-full object-cover" loading="lazy" />}
              <div className="absolute inset-x-0 bottom-0 p-1 bg-black/60 flex justify-center">
                <MoveMenu it={it} albums={albums} onMove={async (aid) => { try { await api.putAuth(`/api/admin/gallery/files/${it.file_id}`, { album_id: aid }, token); toast.success('Moved into album'); load(); onChanged(); } catch (e) { toast.error(e.message); } }} />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export function GalleryAdminTab({ token, role }) {
  const [albums, setAlbums] = useState(null);
  const [canPublish, setCanPublish] = useState(['admin', 'chairman'].includes(role));
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: '', event_date: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [current, setCurrent] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    try { const d = await api.getAuth('/api/admin/gallery/albums', token); setAlbums(d.albums || []); setCanPublish(!!d.can_publish); }
    catch (e) { toast.error(e.message); setAlbums([]); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const d = await api.postAuth('/api/admin/gallery/albums', form, token);
      toast.success('Album created — now add photos');
      setCreating(false); setForm({ title: '', event_date: '', description: '' });
      await load(); setCurrent(d.album);
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };
  const publish = async (a) => { try { await api.postAuth(`/api/admin/gallery/albums/${a.album_id}/publish`, {}, token); toast.success(`“${a.title}” is now live on the Gallery page`); load(); } catch (e) { toast.error(e.message); } };
  const unpublish = async (a) => { try { await api.postAuth(`/api/admin/gallery/albums/${a.album_id}/unpublish`, {}, token); toast.success('Album hidden from the public site'); load(); } catch (e) { toast.error(e.message); } };
  const doDelete = async () => {
    const a = confirmDelete; setConfirmDelete(null);
    try { await api.deleteAuth(`/api/admin/gallery/albums/${a.album_id}`, token); toast.success('Album removed — photos kept in Unsorted'); load(); } catch (e) { toast.error(e.message); }
  };

  if (current) {
    return <AlbumEditor token={token} album={current} albums={albums || []} canPublish={canPublish} onBack={() => { setCurrent(null); load(); }} onChanged={load} />;
  }

  return (
    <div className="space-y-6" data-testid="gallery-admin-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Gallery Albums</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Organise photos into albums, add captions, pick a cover, then publish. {canPublish ? '' : 'Only admins and the chairman can publish albums to the public site.'}</p>
        </div>
        <div className="flex gap-2">
          <a href="/gallery" target="_blank" rel="noreferrer" className="text-sm underline flex items-center gap-1 self-center" data-testid="gallery-open-public">Public gallery <ExternalLink className="w-3.5 h-3.5" /></a>
          <Button onClick={() => setCreating(true)} className="bg-[hsl(var(--gs-navy))]" data-testid="gallery-new-album"><Plus className="w-4 h-4 mr-2" /> New album</Button>
        </div>
      </div>

      {albums === null ? <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[0, 1, 2].map(i => <Skeleton key={i} className="h-64 rounded-xl" />)}</div>
        : albums.length === 0 ? (
          <Card className="p-10 text-center" data-testid="gallery-admin-empty"><FolderOpen className="w-10 h-10 mx-auto text-[hsl(var(--muted-foreground))]" /><p className="mt-3 text-[hsl(var(--muted-foreground))]">No albums yet. Create one for each event (e.g. “Navratri 2025”) and upload photos into it.</p><Button className="mt-4 bg-[hsl(var(--gs-navy))]" onClick={() => setCreating(true)}><Plus className="w-4 h-4 mr-2" /> Create first album</Button></Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="gallery-admin-albums">
            {albums.map(a => <AlbumCard key={a.album_id} a={a} canPublish={canPublish} onOpen={setCurrent} onPublish={publish} onUnpublish={unpublish} onDelete={setConfirmDelete} />)}
          </div>
        )}

      <UnsortedPanel token={token} albums={albums || []} onChanged={load} />

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent data-testid="gallery-create-dialog">
          <DialogHeader><DialogTitle>New album</DialogTitle><DialogDescription>Albums start as drafts. Add photos, then publish when ready.</DialogDescription></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div><Label htmlFor="new-alb-title">Title *</Label><Input id="new-alb-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Diwali Celebration 2025" required data-testid="gallery-create-title" /></div>
            <div><Label htmlFor="new-alb-date">Event date</Label><Input id="new-alb-date" type="date" value={form.event_date} onChange={e => setForm({ ...form, event_date: e.target.value })} data-testid="gallery-create-date" /></div>
            <div><Label htmlFor="new-alb-desc">Description</Label><Textarea id="new-alb-desc" rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="gallery-create-description" /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setCreating(false)}>Cancel</Button><Button type="submit" disabled={saving || !form.title.trim()} className="bg-[hsl(var(--gs-navy))]" data-testid="gallery-create-submit">{saving ? 'Creating…' : 'Create album'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent data-testid="gallery-delete-dialog">
          <DialogHeader><DialogTitle>Delete album “{confirmDelete?.title}”?</DialogTitle><DialogDescription>The album is removed from the website. Its photos are <strong>not</strong> deleted — they move to “Unsorted” so you can reuse them.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button><Button variant="destructive" onClick={doDelete} data-testid="gallery-delete-confirm">Delete album</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
